import { CompleteKundali, Language, PlanetPosition } from '../types/kundali';
import { LifeThemeId, LifeThemeQuestion, BhavaAssessmentDetail, KarakaAssessmentDetail, PhalaBhutaWindow } from '../types/advancedLifeAnalysis';
import { formatDate } from './dateUtils';

// Helper to determine planet strength map
export interface ChartSynthesizedFactors {
  lagnaSign: string;
  lagnaLord: string;
  tenthLord: string;
  tenthLordHouse: number;
  tenthLordDignity: string;
  secondLord: string;
  eleventhLord: string;
  fourthLord: string;
  seventhLord: string;
  fifthLord: string;
  ninthLord: string;
  sixthLord: string;
  twelfthLord: string;
  sunDignity: string;
  sunHouse: number;
  marsDignity: string;
  marsHouse: number;
  mercuryDignity: string;
  mercuryHouse: number;
  jupiterDignity: string;
  jupiterHouse: number;
  venusDignity: string;
  venusHouse: number;
  saturnDignity: string;
  saturnHouse: number;
  rahuHouse: number;
  ketuHouse: number;
  amatyakarakaPlanet?: string;
  darakarakaPlanet?: string;
  putrakarakaPlanet?: string;
  atmakarakaPlanet?: string;
  hasSunGovtYoga: boolean;
  hasMarsExecutiveYoga: boolean;
  hasCorporateMncYoga: boolean;
  hasBusinessEnterpriseYoga: boolean;
  hasRealEstateWealthYoga: boolean;
  hasAdvisoryConsultingYoga: boolean;
  hasDhanaYoga: boolean;
  primaryIncomeStreamsEn: string[];
  primaryIncomeStreamsHi: string[];
  govVsPrivateVerdictEn: string;
  govVsPrivateVerdictHi: string;
  govVsPrivateDetailEn: string;
  govVsPrivateDetailHi: string;
}

const SIGN_LORDS: Record<string, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

export function extractChartSynthesisFactors(kundali: CompleteKundali): ChartSynthesizedFactors {
  const planets = kundali.planets || [];
  const ascendant = planets.find((p) => p.name === 'Ascendant');
  const lagnaSign = ascendant ? ascendant.sign : 'Aries';
  const lagnaIdx = Math.max(0, (SIGN_NAMES || []).indexOf(lagnaSign));

  const getHouseLord = (hNum: number): string => {
    const sIdx = (lagnaIdx + (hNum - 1)) % 12;
    return SIGN_LORDS[SIGN_NAMES[sIdx]] || 'Sun';
  };

  const getPlanet = (name: string) => planets.find((p) => p.name === name);

  const lagnaLord = getHouseLord(1);
  const secondLord = getHouseLord(2);
  const fourthLord = getHouseLord(4);
  const fifthLord = getHouseLord(5);
  const sixthLord = getHouseLord(6);
  const seventhLord = getHouseLord(7);
  const ninthLord = getHouseLord(9);
  const tenthLord = getHouseLord(10);
  const eleventhLord = getHouseLord(11);
  const twelfthLord = getHouseLord(12);

  const sun = getPlanet('Sun');
  const mars = getPlanet('Mars');
  const mercury = getPlanet('Mercury');
  const jupiter = getPlanet('Jupiter');
  const venus = getPlanet('Venus');
  const saturn = getPlanet('Saturn');
  const rahu = getPlanet('Rahu');
  const ketu = getPlanet('Ketu');

  const tenthLordPlanet = getPlanet(tenthLord);
  const tenthLordHouse = tenthLordPlanet ? tenthLordPlanet.house : 10;
  const tenthLordDignity = tenthLordPlanet?.dignity || 'Direct';

  // Chara karakas from degrees
  const planetsWithDegrees = planets
    .filter((p) => !['Ascendant', 'Rahu', 'Ketu'].includes(p.name))
    .map((p) => {
      const deg = typeof p.degreeInSign === 'number' ? p.degreeInSign : 15;
      return { name: p.name, deg };
    })
    .sort((a, b) => b.deg - a.deg);

  const atmakarakaPlanet = planetsWithDegrees[0]?.name || 'Sun';
  const amatyakarakaPlanet = planetsWithDegrees[1]?.name || 'Mercury';
  const putrakarakaPlanet = planetsWithDegrees[4]?.name || 'Jupiter';
  const darakarakaPlanet = planetsWithDegrees[planetsWithDegrees.length - 1]?.name || 'Venus';

  // Government / Authority Indicators
  const isSunStrong = Boolean(sun && ([1, 10, 9, 5].includes(sun.house) || sun.dignity === 'Exalted' || sun.dignity === 'Own House'));
  const isMarsStrong = Boolean(mars && ([1, 10, 6, 9].includes(mars.house) || mars.dignity === 'Exalted' || mars.dignity === 'Own House'));
  const isTenthConnectedToSunMars = [tenthLord, amatyakarakaPlanet].includes('Sun') || [tenthLord, amatyakarakaPlanet].includes('Mars');

  const hasSunGovtYoga = Boolean(isSunStrong && (isTenthConnectedToSunMars || [10, 1, 9].includes(sun?.house || 0)));
  const hasMarsExecutiveYoga = Boolean(isMarsStrong && (mars?.house === 10 || mars?.house === 1 || tenthLord === 'Mars'));

  // Corporate / MNC / IT / Analytics Indicators
  const isMercuryStrong = Boolean(mercury && ([1, 10, 11, 2, 5].includes(mercury.house) || mercury.dignity === 'Exalted' || mercury.dignity === 'Own House'));
  const isSaturnStrong = Boolean(saturn && ([10, 11, 1, 7].includes(saturn.house) || saturn.dignity === 'Exalted' || saturn.dignity === 'Own House'));
  const hasCorporateMncYoga = Boolean(isMercuryStrong || isSaturnStrong || amatyakarakaPlanet === 'Mercury' || amatyakarakaPlanet === 'Saturn' || [10, 11].includes(rahu?.house || 0));

  // Business / Enterprise Indicators
  const seventhLordPlanet = getPlanet(seventhLord);
  const eleventhLordPlanet = getPlanet(eleventhLord);
  const fourthLordPlanet = getPlanet(fourthLord);
  const secondLordPlanet = getPlanet(secondLord);
  const twelfthLordPlanet = getPlanet(twelfthLord);

  const hasBusinessEnterpriseYoga = Boolean(
    (seventhLordPlanet && [7, 10, 11].includes(seventhLordPlanet.house)) ||
    (mercury && [7, 11].includes(mercury.house)) ||
    (eleventhLordPlanet && [1, 7, 11].includes(eleventhLordPlanet.house))
  );

  // Real Estate / Land Wealth
  const isFourthStrong = Boolean(fourthLordPlanet && [4, 1, 9, 11].includes(fourthLordPlanet.house));
  const hasRealEstateWealthYoga = Boolean(
    isFourthStrong &&
    (mars?.house === 4 || mars?.house === 11 || (saturn && [4, 10, 11].includes(saturn.house)) || fourthLord === 'Mars')
  );

  // Advisory / Consulting / Financial Markets
  const isJupiterStrong = Boolean(jupiter && ([1, 5, 9, 10, 11, 2].includes(jupiter.house) || jupiter.dignity === 'Exalted' || jupiter.dignity === 'Own House'));
  const hasAdvisoryConsultingYoga = Boolean(
    isJupiterStrong &&
    ([2, 5, 9, 10, 11].includes(jupiter?.house || 0) || tenthLord === 'Jupiter' || amatyakarakaPlanet === 'Jupiter')
  );

  // Dhana Yoga Combinations (2, 5, 9, 11 mutual connections)
  const hasDhanaYoga = Boolean(
    (secondLordPlanet && [2, 11, 9, 5, 1].includes(secondLordPlanet.house)) ||
    (eleventhLordPlanet && [2, 11, 9, 5, 1].includes(eleventhLordPlanet.house)) ||
    isJupiterStrong ||
    isMercuryStrong
  );

  // Concrete Primary Wealth Streams Formulation
  const primaryIncomeStreamsEn: string[] = [];
  const primaryIncomeStreamsHi: string[] = [];

  // Stream 1: Primary Career / Professional
  if (hasCorporateMncYoga || [10, 6, 1].includes(tenthLordHouse)) {
    primaryIncomeStreamsEn.push('Executive salary, professional leadership bonuses & structured corporate compensation');
    primaryIncomeStreamsHi.push('व्यावसायिक पद, कॉर्पोरेट वेतन, नेतृत्व इंसेंटिव एवं नियमित सेवा आय');
  } else if (hasSunGovtYoga) {
    primaryIncomeStreamsEn.push('Government / Administrative executive pay, institutional emoluments & authoritative perks');
    primaryIncomeStreamsHi.push('प्रशासनिक/शासकीय पद, संस्थागत वेतन व भत्ते');
  } else {
    primaryIncomeStreamsEn.push('Core professional vocation & specialized consulting earnings');
    primaryIncomeStreamsHi.push('मूल कार्यक्षेत्र, व्यावसायिक सेवा एवं परामर्श आय');
  }

  // Stream 2: Business / Commercial vs Real Estate
  if (hasBusinessEnterpriseYoga) {
    primaryIncomeStreamsEn.push('Commercial enterprise profits, trade margins & independent business ventures');
    primaryIncomeStreamsHi.push('व्यापारिक उद्यम, स्वतंत्र व्यवसाय, वाणिज्यिक लाभ एवं साझेदारी मुनाफा');
  } else if (hasRealEstateWealthYoga) {
    primaryIncomeStreamsEn.push('Real estate asset appreciation, land investments & commercial/residential rental yields');
    primaryIncomeStreamsHi.push('भूमि/भवन निवेश, अचल संपत्ति मूल्यवृद्धि एवं नियमित किराया आय');
  } else {
    primaryIncomeStreamsEn.push('Strategic capital investments, dividends & calculated financial growth');
    primaryIncomeStreamsHi.push('दीर्घकालिक पूंजी निवेश, लाभांश एवं सुरक्षित बचत वृद्धि');
  }

  // Stream 3: Advisory / Intellectual / Property
  if (hasAdvisoryConsultingYoga) {
    primaryIncomeStreamsEn.push('Knowledge consultancy, advisory retainers, royalties & strategic guidance fees');
    primaryIncomeStreamsHi.push('ज्ञान आधारित परामर्श, विशेषज्ञ सलाह, रायल्टी एवं बौद्धिक शुल्क');
  } else if (!hasRealEstateWealthYoga) {
    primaryIncomeStreamsEn.push('Built property equity, domestic assets & secondary capital gains');
    primaryIncomeStreamsHi.push('अचल संपत्ति, पारिवारिक संपदा एवं द्वितीयक पूंजीगत लाभ');
  }

  // Government vs Private Verdict Formulation
  let govVsPrivateVerdictEn = 'Strong Suitability for Private Corporate Sector & Executive Leadership';
  let govVsPrivateVerdictHi = 'निजी कॉर्पोरेट क्षेत्र, बहुराष्ट्रीय कंपनियों एवं कार्यकारी प्रबंधन में सर्वाधिक सफलता';
  let govVsPrivateDetailEn = `Your astrological profile indicates strong adaptability, strategic problem-solving, and commercial insight. You will experience fast career growth, high compensation, and greater satisfaction in dynamic corporate roles, technology enterprises, and leadership hierarchies.`;
  let govVsPrivateDetailHi = `आपकी कुंडली कॉर्पोरेट रणनीति, प्रबंधन कुशलता और तकनीकी व्यावसायिक क्षेत्र में तीव्र उन्नति और उच्च पद-प्रतिष्ठा का स्पष्ट संकेत देती है।`;

  if (hasSunGovtYoga && hasMarsExecutiveYoga) {
    govVsPrivateVerdictEn = 'High Favorability for Government Administration & Public Sector Leadership';
    govVsPrivateVerdictHi = 'सरकारी प्रशासनिक सेवा, सिविल सर्विसेज, पीएसयू एवं शासकीय प्राधिकार में प्रबल योग';
    govVsPrivateDetailEn = `Your planetary indications show natural leadership, high sense of public duty, and policy acumen. You have very strong potential for prestigious administrative roles, public sector leadership, or stable government services where you can hold long-term executive responsibility.`;
    govVsPrivateDetailHi = `आपकी कुंडली में राजकीय प्राधिकार, प्रशासनिक सेवा एवं सार्वजनिक उपक्रमों में सम्मानित पद और दीर्घकालिक स्थिरता का अत्यंत प्रबल योग है।`;
  } else if (hasSunGovtYoga) {
    govVsPrivateVerdictEn = 'Strong Alignment with Public Sector Undertakings (PSU) & Regulated Institutions';
    govVsPrivateVerdictHi = 'सार्वजनिक उपक्रमों (PSU), स्वायत्त शासकीय बोर्डों, बैंकिंग एवं अर्ध-सरकारी संस्थानों में अनुकूलता';
    govVsPrivateDetailEn = `Your astrological configurations favor structured institutional authority, public sector enterprises, and regulated advisory positions over high-volatility private ventures.`;
    govVsPrivateDetailHi = `आपकी ग्रह स्थिति शासकीय उपक्रमों, वित्तीय संस्थानों और संस्थागत नेतृत्व में उत्कृष्ट सम्मान और स्थायित्व का योग बनाती है।`;
  }

  return {
    lagnaSign,
    lagnaLord,
    tenthLord,
    tenthLordHouse,
    tenthLordDignity,
    secondLord,
    eleventhLord,
    fourthLord,
    seventhLord,
    fifthLord,
    ninthLord,
    sixthLord,
    twelfthLord,
    sunDignity: sun?.dignity || 'Direct',
    sunHouse: sun?.house || 1,
    marsDignity: mars?.dignity || 'Direct',
    marsHouse: mars?.house || 1,
    mercuryDignity: mercury?.dignity || 'Direct',
    mercuryHouse: mercury?.house || 1,
    jupiterDignity: jupiter?.dignity || 'Direct',
    jupiterHouse: jupiter?.house || 1,
    venusDignity: venus?.dignity || 'Direct',
    venusHouse: venus?.house || 1,
    saturnDignity: saturn?.dignity || 'Direct',
    saturnHouse: saturn?.house || 1,
    rahuHouse: rahu?.house || 1,
    ketuHouse: ketu?.house || 1,
    amatyakarakaPlanet,
    darakarakaPlanet,
    putrakarakaPlanet,
    atmakarakaPlanet,
    hasSunGovtYoga,
    hasMarsExecutiveYoga,
    hasCorporateMncYoga,
    hasBusinessEnterpriseYoga,
    hasRealEstateWealthYoga,
    hasAdvisoryConsultingYoga,
    hasDhanaYoga,
    primaryIncomeStreamsEn,
    primaryIncomeStreamsHi,
    govVsPrivateVerdictEn,
    govVsPrivateVerdictHi,
    govVsPrivateDetailEn,
    govVsPrivateDetailHi
  };
}

export interface QuestionSynthesisOutput {
  verdictEn: string;
  verdictHi: string;
  analysisSummaryEn: string;
  analysisSummaryHi: string;
  primaryMechanismEn: string;
  primaryMechanismHi: string;
  overallFeedbackEn: string;
  overallFeedbackHi: string;
}

/**
 * Synthesizes razor-sharp, question-specific answers for every life inquiry
 */
export function synthesizeLifeQuestionAnswer(
  question: LifeThemeQuestion,
  themeId: LifeThemeId,
  factors: ChartSynthesizedFactors,
  promiseScore: number,
  primaryWindow: PhalaBhutaWindow,
  kundali: CompleteKundali
): QuestionSynthesisOutput {
  const qId = question.id;
  const pStart = formatDate(primaryWindow.startDate);
  const pEnd = formatDate(primaryWindow.endDate);
  const md = primaryWindow.mahadashaLord;
  const ad = primaryWindow.antardashaLord;

  // 1. CAREER & JOB THEME
  if (qId === 'career_timing_promotion_job_change') {
    const verdictEn = promiseScore >= 75
      ? `Major Career Breakthrough & Upward Promotion Window (${pStart} to ${pEnd})`
      : `Favorable Career Transition & Advancement Window (${pStart} to ${pEnd})`;
    const verdictHi = promiseScore >= 75
      ? `पदोन्नति, नई नौकरी व करियर में बड़े उत्थान का प्रबल समय (${pStart} से ${pEnd})`
      : `सकारात्मक पद परिवर्तन व कार्यक्षेत्र में प्रगति का अनुकूल काल (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `Your astrological cycles are currently aligned for career progress. This phase brings positive interview outcomes, recognition from senior leadership, and opportunities for elevated responsibilities and higher pay. Focus on presenting your achievements clearly and expanding your professional network.`;
    const analysisSummaryHi = `आपकी ग्रह दशा वर्तमान में करियर में उन्नति के लिए अनुकूल है। यह समय नई नौकरी के अच्छे प्रस्ताव, कार्यस्थल पर पदोन्नति और सम्मानजनक पदभार मिलने के लिए बहुत फलदायी रहेगा।`;

    const primaryMechanismEn = `The active planetary cycles between ${pStart} and ${pEnd} provide strong momentum for career growth and transitions.`;
    const primaryMechanismHi = `${pStart} से ${pEnd} के मध्य का समय कार्यक्षेत्र में उन्नति और सकारात्मक बदलाव के लिए सबसे अनुकूल है।`;

    const overallFeedbackEn = `Prepare your resume, initiate active networking, and target higher leadership positions during this active window. Approach interviews and discussions with quiet confidence.`;
    const overallFeedbackHi = `इस अनुकूल समय में अपने बायोडाटा को अपडेट करें और नए अवसरों के लिए सक्रिय रहें। आत्मविश्वास के साथ आगे बढ़ें।`;

    return { verdictEn, verdictHi, analysisSummaryEn, analysisSummaryHi, primaryMechanismEn, primaryMechanismHi, overallFeedbackEn, overallFeedbackHi };
  }

  if (qId === 'career_govt_vs_private') {
    return {
      verdictEn: factors.govVsPrivateVerdictEn,
      verdictHi: factors.govVsPrivateVerdictHi,
      analysisSummaryEn: factors.govVsPrivateDetailEn,
      analysisSummaryHi: factors.govVsPrivateDetailHi,
      primaryMechanismEn: `Your inherent strengths align naturally with this sector, ensuring long-term stability and success.`,
      primaryMechanismHi: `आपकी स्वाभाविक कार्यकुशलता और ग्रह स्थिति इस क्षेत्र में निरंतर प्रगति का मार्ग प्रशस्त करती है।`,
      overallFeedbackEn: `Focus your skill acquisition and competitive preparation toward the identified dominant sector to maximize long-term authority and career fulfillment.`,
      overallFeedbackHi: `अपनी ऊर्जा और कौशल को चिन्हित अनुकूल क्षेत्र पर केंद्रित करें जिससे अधिकतम प्रतिष्ठा और आर्थिक लाभ प्राप्त हो सके।`
    };
  }

  if (qId === 'career_long_term_peak') {
    const verdictEn = promiseScore >= 75
      ? 'Executive Authority, Senior Leadership & High Professional Standing'
      : 'Specialized Expertise, Respected Mentorship & Solid Professional Standing';
    const verdictHi = promiseScore >= 75
      ? 'शीर्ष कार्यकारी नेतृत्व, प्रशासनिक अधिकार एवं उच्च सामाजिक प्रतिष्ठा'
      : 'विशिष्ट विशेषज्ञता, सम्मानित सलाहकार पद एवं सुदृढ़ व्यावसायिक स्थिति';

    const analysisSummaryEn = `Your chart highlights long-term leadership, trusted decision-making, and deep domain mastery. Over the course of your career, you are naturally suited to rise into executive leadership, mentoring roles, or leading strategic initiatives with wide professional respect.`;
    const analysisSummaryHi = `आपकी कुंडली में उच्च पद, नीतिगत निर्णय लेने और टीम का मार्गदर्शन करने की स्वाभाविक क्षमता है। आप अपने कार्यक्षेत्र में महत्वपूर्ण नेतृत्व और सम्मानजनक स्थिति प्राप्त करेंगे।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Consistent dedication and strategic skill-building will steadily guide you to your career's highest peak.`,
      primaryMechanismHi: `अनुभव और निरंतर प्रयासों से आप समय के साथ अपने कार्यक्षेत्र में सर्वोच्च शिखर को प्राप्त करेंगे।`,
      overallFeedbackEn: `Invest in long-term strategic vision, executive presence, and continuous professional credibility to sustain this peak trajectory.`,
      overallFeedbackHi: `दीर्घकालिक दूरदृष्टि और नेतृत्व क्षमता का निरंतर विकास करें ताकि यह प्रतिष्ठा चिरस्थायी बनी रहे।`
    };
  }

  if (qId === 'career_switch_or_stay') {
    const isReady = promiseScore >= 60;
    const verdictEn = isReady
      ? 'Favorable Time to Actively Explore & Switch Roles'
      : 'Consolidate Current Role for Now; Plan Next Move Methodically';
    const verdictHi = isReady
      ? 'नौकरी बदलने और नए अवसर तलाशने का अनुकूल समय'
      : 'वर्तमान पद पर स्थिरता बनाए रखें; योजनाबद्ध तरीके से आगे बढ़ें';

    const analysisSummaryEn = isReady
      ? `Current astrological trends favor actively exploring new roles and interviewing. Keep scouting opportunities, and ensure you have a confirmed written offer before transitioning.`
      : `Current indications suggest avoiding hasty resignations. Build your skills, strengthen your network, and prepare methodically for a smooth transition in the upcoming phase.`;
    const analysisSummaryHi = isReady
      ? `वर्तमान समय नई भूमिका तलाशने, साक्षात्कार देने और पदोन्नति के साथ नौकरी बदलने के लिए अनुकूल है। नए प्रस्ताव के बाद ही परिवर्तन करें।`
      : `वर्तमान में बिना नए प्रस्ताव के नौकरी छोड़ने से बचें। अपने कौशल को निखारें और योजनाबद्ध तरीके से आगे बढ़ें।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Current professional energies support careful planning and secure career progression.`,
      primaryMechanismHi: `वर्तमान समय समझदारी से योजना बनाकर सुरक्षित तरीके से आगे बढ़ने का संकेत देता है।`,
      overallFeedbackEn: `Secure a formal appointment letter before parting with current responsibilities and negotiate compensation based on market benchmarks.`,
      overallFeedbackHi: `नए पद का आधिकारिक नियुक्ति पत्र प्राप्त होने के पश्चात ही वर्तमान पद से विदा लें।`
    };
  }

  // 2. WEALTH & FINANCE THEME
  if (qId === 'wealth_primary_sources_magnitude') {
    const stream1 = factors.primaryIncomeStreamsEn[0] || 'Professional salary & executive compensation';
    const stream2 = factors.primaryIncomeStreamsEn[1] || 'Real estate investments & asset appreciation';
    const stream3 = factors.primaryIncomeStreamsEn[2] || 'Consulting advisory & diversified investment yields';

    const stream1Hi = factors.primaryIncomeStreamsHi[0] || 'नियमित व्यावसायिक वेतन एवं कार्यक्षेत्र से आय';
    const stream2Hi = factors.primaryIncomeStreamsHi[1] || 'अचल संपत्ति, भूमि-भवन निवेश एवं किराया आय';
    const stream3Hi = factors.primaryIncomeStreamsHi[2] || 'परामर्श शुल्क एवं सुरक्षित पूंजीगत लाभ';

    const verdictEn = promiseScore >= 75
      ? 'Substantial Multi-Stream Wealth via Career Leadership, Real Estate & Prudent Investments'
      : 'Steady Financial Growth via Professional Service & Consistent Asset Building';
    const verdictHi = promiseScore >= 75
      ? 'करियर, अचल संपत्ति व रणनीतिक निवेश से बहु-स्रोतीय प्रचुर धन अर्जन'
      : 'व्यावसायिक सेवा एवं अनुशासित बचत द्वारा स्थिर एवं सुरक्षित धन संचय';

    const analysisSummaryEn = `Your primary avenues for building long-term wealth are: 1. ${stream1}, 2. ${stream2}, and 3. ${stream3}. Your chart demonstrates high financial resilience, sound capital preservation, and steady compounding growth over time.`;
    const analysisSummaryHi = `आपकी कुंडली में धन प्राप्ति के मुख्य ठोस स्रोत: १. ${stream1Hi}, २. ${stream2Hi}, एवं ३. ${stream3Hi} हैं। आपकी ग्रह स्थिति सुरक्षित बचत और दीर्घकालिक संपत्ति निर्माण को मजबूती प्रदान करती है।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Multiple income streams and disciplined savings will steadily build your overall net worth.`,
      primaryMechanismHi: `आय के विभिन्न स्रोतों और नियमित बचत से आपकी वित्तीय स्थिति निरंतर मजबूत होगी।`,
      overallFeedbackEn: `Diversify savings into tangible fixed assets (property/land) and systematic investment funds. Maintain a 6-month emergency liquidity reserve.`,
      overallFeedbackHi: `अपनी बचत को अचल संपत्तियों और सुरक्षित निवेश योजनाओं में विभाजित करें तथा अनावश्यक जोखिम से बचें।`
    };
  }

  if (qId === 'wealth_timing_peak_inflow') {
    const verdictEn = `Major Inflow & Peak Wealth Accumulation Phase (${pStart} to ${pEnd})`;
    const verdictHi = `प्रचुर धन लाभ, कोष वृद्धि एवं आर्थिक उत्थान का मुख्य काल (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period between ${pStart} and ${pEnd} is particularly supportive for financial growth, salary increments, investment gains, or expanding your business revenue.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के दौरान धन लाभ के अच्छे अवसर, वेतन वृद्धि और संचित कोष में बढ़ोतरी के सकारात्मक योग बन रहे हैं।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Supportive astrological cycles between ${pStart} and ${pEnd} unlock favorable financial opportunities.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के मध्य का समय आर्थिक लाभ और कोष वृद्धि के लिए अनुकूल रहेगा।`,
      overallFeedbackEn: `Channel surplus earnings directly into wealth-generating assets rather than speculative lifestyle expenditures during this fruitful window.`,
      overallFeedbackHi: `इस शुभ समय में प्राप्त अतिरिक्त धन को सुरक्षित दीर्घकालिक संपत्तियों में लगाएं।`
    };
  }

  if (qId === 'wealth_loss_debt_risks') {
    const verdictEn = promiseScore >= 60
      ? 'Low Inherent Debt Risk with Strong Financial Capital Defense'
      : 'Moderate Expenditure Pressure; Maintain Practical Budgetary Safeguards';
    const verdictHi = promiseScore >= 60
      ? 'ऋण (कर्ज) का निम्न जोखिम एवं सुदृढ़ वित्तीय सुरक्षा'
      : 'व्यय पर नियंत्रण आवश्यक; सट्टा व अनियोजित जोखिम से दूरी रखें';

    const analysisSummaryEn = `Your chart shows manageable financial commitments. To keep your wealth secure, avoid informal lending without legal documentation, stay clear of speculative day trading, and keep borrowings strictly limited to value-appreciating assets like a home.`;
    const analysisSummaryHi = `आपकी कुंडली में वित्तीय स्थिति संतुलित है। बिना लिखा-पढ़ी के किसी को उधार देने और अत्यधिक जोखिम वाले सट्टे से बचें। केवल लाभकारी संपत्तियों (जैसे गृह ऋण) के लिए ही कर्ज लें।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Prudent budgeting and conservative investment habits protect you against unexpected financial pressure.`,
      primaryMechanismHi: `अनुशासित बजट और सुरक्षित निवेश से आपके धन की सुरक्षा सदैव बनी रहेगी।`,
      overallFeedbackEn: `Automate your monthly savings before discretionary spending and review all financial contracts diligently.`,
      overallFeedbackHi: `मासिक खर्चों का उचित बजट बनाएं और किसी भी वित्तीय अनुबंध पर हस्ताक्षर करने से पूर्व भली-भांति जांच करें।`
    };
  }

  // 3. MARRIAGE & RELATIONSHIP THEME
  if (qId === 'marriage_timing_window') {
    const verdictEn = `Prime Auspicious Window for Marriage & Lifelong Alliance (${pStart} to ${pEnd})`;
    const verdictHi = `विवाह, सगाई एवं वैवाहिक गठबंधन का सर्वाधिक शुभ व प्रबल काल (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period between ${pStart} and ${pEnd} brings supportive planetary harmony for finding a compatible match, receiving positive proposals, and celebrating marital milestones with family blessings.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के मध्य विवाह योग्य अनुकूल प्रस्तावों, सगाई और पारिवारिक सहमति से विवाह संपन्न होने का अत्यंत शुभ योग बन रहा है।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Supportive relationship cycles between ${pStart} and ${pEnd} create the ideal environment for marriage.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के दौरान ग्रह स्थिति विवाह बंधन को साकार करने में पूर्ण सहायक है।`,
      overallFeedbackEn: `Be open to introductions through respected family channels or professional circles during this auspicious window.`,
      overallFeedbackHi: `इस शुभ समय में आने वाले विवाह प्रस्तावों पर खुले मन और सकारात्मक दृष्टिकोण से विचार करें।`
    };
  }

  if (qId === 'marriage_spouse_characteristics') {
    const verdictEn = 'Cultured, Intellectually Refined, Dignified & Emotionally Supportive Partner';
    const verdictHi = 'संस्कारी, बौद्धिक रूप से प्रबुद्ध, गरिमामय एवं भावनात्मक रूप से समर्पित जीवनसाथी';

    const analysisSummaryEn = `Your future life partner is indicated to be cultured, thoughtful, responsible, and emotionally supportive. They will possess good moral values, refined tastes, and practical wisdom, bringing warmth, mutual understanding, and balance to your shared life.`;
    const analysisSummaryHi = `आपके भावी जीवनसाथी सुशिक्षित, स्वाभिमानी, पारिवारिक मूल्यों का सम्मान करने वाले और व्यावहारिक सोच के धनी होंगे। वे आपके जीवन में संतुलन और आत्मीय सुख का संचार करेंगे।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Your astrological profile highlights enduring compatibility and shared life values with your spouse.`,
      primaryMechanismHi: `ग्रह स्थिति जीवनसाथी के साथ आपसी समझ और उदात्त गुणों के सुंदर समन्वय को दर्शाती है।`,
      overallFeedbackEn: `Nurture open communication and mutual respect to build a deeply fulfilling lifelong partnership.`,
      overallFeedbackHi: `पारस्परिक सम्मान, स्पष्ट संवाद और एक-दूसरे के विचारों को महत्व देकर दांपत्य को सुदृढ़ बनाएं।`
    };
  }

  if (qId === 'marriage_harmony_longevity') {
    const verdictEn = 'High Domestic Harmony, Mutual Trust & Enduring Marital Stability';
    const verdictHi = 'उत्तम दांपत्य सुख, परस्पर विश्वास एवं दीर्घकालिक वैवाहिक स्थायित्व';

    const analysisSummaryEn = `Your chart indicates a warm, supportive marital bond with strong mutual respect and loyalty. Both of you will share practical understanding and emotional support, ensuring lasting peace and stability in your domestic life.`;
    const analysisSummaryHi = `आपकी कुंडली में दांपत्य जीवन के लिए मधुरता और स्थायित्व का सुंदर योग है। दोनों के बीच आपसी समझ, विश्वास और सम्मान किसी भी परिस्थिति में दांपत्य को सुखी बनाए रखेगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Mutual respect, shared values, and balanced communication ensure enduring harmony.`,
      primaryMechanismHi: `आपसी समझ और सकारात्मक संवाद से दांपत्य में सुख-शांति सदैव बनी रहेगी।`,
      overallFeedbackEn: `Prioritize quality time together and resolve differences through calm, empathetic dialogue.`,
      overallFeedbackHi: `दैनिक जीवन में एक-दूसरे के लिए समय निकालें और किसी भी मतभेद को शांतिपूर्ण बातचीत से सुलझाएं।`
    };
  }

  if (qId === 'marriage_obstacles_remedies') {
    const verdictEn = 'Constructive Maturation Phase; Minimal Delays Responsive to Simple Spiritual Practices';
    const verdictHi = 'सकारात्मक परिपक्वता काल; सरल प्रार्थना एवं सकारात्मक सोच से बाधाओं का समाधान';

    const analysisSummaryEn = `Any temporary delays in marriage have been a natural phase of maturation and careful partner selection. Cultivating a calm mind, simple spiritual reflection, and open family communication will smoothly dissolve remaining uncertainties.`;
    const analysisSummaryHi = `विवाह में हल्का विलंब केवल उपयुक्त जीवनसाथी के सही चयन की प्रक्रिया का हिस्सा है। सकारात्मक दृष्टिकोण और नियमित प्रार्थना से सभी अड़चनें दूर होकर अनुकूल संबंध तय होंगे।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Upcoming supportive phases help remove past hesitations and open clear matrimonial paths.`,
      primaryMechanismHi: `आने वाला समय विवाह से जुड़ी सभी चिंताओं को दूर कर नई शुरुआत का मार्ग प्रशस्त करेगा।`,
      overallFeedbackEn: `Practice regular morning prayers, maintain healthy family communication, and keep an open, positive outlook.`,
      overallFeedbackHi: `नियमित प्रार्थना करें, पारिवारिक चर्चाओं में सकारात्मक रहें और आशावादी दृष्टिकोण बनाए रखें।`
    };
  }

  // 4. HOUSE & REAL ESTATE THEME
  if (qId === 'property_timing_buy_construct') {
    const verdictEn = `Auspicious Window for Home Purchase & Construction (${pStart} to ${pEnd})`;
    const verdictHi = `अपना घर/फ्लैट खरीदने एवं गृह निर्माण का अत्यंत शुभ योग काल (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period from ${pStart} to ${pEnd} is strongly favorable for buying a house, booking an apartment, or starting home construction with smooth loan approvals and family joy.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} का समय नया मकान, फ्लैट या भूमि क्रय करने और गृह निर्माण आरंभ करने के लिए अत्यंत अनुकूल और फलदायी रहेगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Supportive property cycles between ${pStart} and ${pEnd} help turn your homeownership aspirations into reality.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के मध्य का समय अपने नाम से संपत्ति और घर बसाने के लिए उत्तम है।`,
      overallFeedbackEn: `Conduct thorough legal verification of property titles and ensure clear documentation before finalizing.`,
      overallFeedbackHi: `संपत्ति के कानूनी दस्तावेजों की भली-भांति जांच करें और शुभ समय में खरीदारी संपन्न करें।`
    };
  }

  if (qId === 'property_investment_suitability') {
    const verdictEn = 'Highly Promising Potential in Real Estate & Built Property Assets';
    const verdictHi = 'रियल एस्टेट, प्लॉट एवं अचल संपत्ति निवेश में अत्यधिक लाभकारी योग';

    const analysisSummaryEn = `Investing in physical land, residential homes, or commercial spaces is naturally well-suited for your profile and acts as a dependable long-term wealth builder. Long-term property holding offers you solid security and steady appreciation.`;
    const analysisSummaryHi = `जमीन, मकान और अचल संपत्तियों में किया गया निवेश आपके लिए सुरक्षित, स्थिर और दीर्घकालिक आर्थिक समृद्धि का सबसे सशक्त माध्यम सिद्ध होगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Tangible physical assets provide strong financial defense and steady compounding value over time.`,
      primaryMechanismHi: `स्थिर संपत्तियां आपके धन को सुरक्षित रखकर समय के साथ निरंतर मूल्य वृद्धि प्रदान करेंगी।`,
      overallFeedbackEn: `Focus on developing suburban corridors or prime commercial parcels with clear title ownership.`,
      overallFeedbackHi: `स्पष्ट मालिकाना हक वाले भूखंडों और विकासशील क्षेत्रों में लंबी अवधि के लिए निवेश करें।`
    };
  }

  // 5. VEHICLE THEME
  if (qId === 'vehicle_timing_purchase') {
    const verdictEn = `Prime Auspicious Window for Vehicle Purchase (${pStart} to ${pEnd})`;
    const verdictHi = `नई कार / वाहन क्रय करने का सर्वोत्तम शुभ समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The phase from ${pStart} to ${pEnd} brings auspicious energy for purchasing a vehicle that offers comfort, safety, and reliable daily travel for you and your family.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} का समय नई गाड़ी खरीदने के लिए बहुत अनुकूल है। यह वाहन आपके लिए सुखद, सुरक्षित और प्रतिष्ठा बढ़ाने वाला सिद्ध होगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Positive mobility cycles between ${pStart} and ${pEnd} facilitate smooth vehicle acquisitions.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के दौरान वाहन प्राप्ति और सुखद यात्रा का उत्तम योग है।`,
      overallFeedbackEn: `Select safety-certified vehicles with reliable engineering and comfortable seating.`,
      overallFeedbackHi: `सुरक्षित और आरामदायक वाहन का चयन करें तथा नियमित रखरखाव पर ध्यान दें।`
    };
  }

  if (qId === 'vehicle_comfort_safety') {
    const verdictEn = 'High Comfort, Reliable Protection & Safe Travel Profile';
    const verdictHi = 'उत्तम आराम, संपूर्ण सुरक्षा एवं सुखद यात्रा योग';

    const analysisSummaryEn = `Your astrological indications favor comfortable, spacious vehicles in elegant shades like white, silver, or blue, offering safe, smooth, and dependable journeys for you and your family.`;
    const analysisSummaryHi = `सफेद, सिल्वर या गहरे नीले रंग का आरामदायक वाहन आपके लिए उपयुक्त रहेगा, जो आपको यात्रा में सुरक्षा और सुखद अनुभव प्रदान करेगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Careful driving and regular maintenance ensure long-lasting vehicular utility and safety.`,
      primaryMechanismHi: `अनुशासित ड्राइविंग और उचित रख-रखाव से यात्राएं सदैव सुरक्षित और सुखद रहेंगी।`,
      overallFeedbackEn: `Keep vehicle documents current and adhere strictly to regular maintenance protocols.`,
      overallFeedbackHi: `वाहन की समय पर सर्विसिंग कराएं और सभी आवश्यक दस्तावेज अद्यतन रखें।`
    };
  }

  // 6. CHILDREN THEME
  if (qId === 'children_timing_conception_birth') {
    const verdictEn = `Favorable Timing Window for Family Growth (${pStart} to ${pEnd})`;
    const verdictHi = `संतान सुख एवं परिवार विस्तार के लिए अनुकूल समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period from ${pStart} to ${pEnd} provides favorable astrological support for family planning and welcoming a child. (For comprehensive medical and personal guidance, consulting healthcare professionals and evaluating both partners' charts is recommended).`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के दौरान संतान सुख और परिवार वृद्धि के लिए सकारात्मक योग बन रहा है। (उत्तम मार्गदर्शन हेतु दंपत्ति का संयुक्त मिलान एवं चिकित्सकीय सलाह उत्तम रहती है)।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Supportive family-growth cycles between ${pStart} and ${pEnd} bring joy and auspicious beginnings.`,
      primaryMechanismHi: `${pStart} से ${pEnd} का समय संतान सुख की दिशा में सकारात्मक प्रगति का मार्ग प्रशस्त करता है।`,
      overallFeedbackEn: `Maintain wholesome nutrition, emotional peace, and regular medical check-ups during this auspicious period.`,
      overallFeedbackHi: `संतुलित खान-पान, मानसिक शांति और नियमित चिकित्सकीय परामर्श का पालन करें।`
    };
  }

  if (qId === 'children_prosperity_education' || qId === 'children_education_career_support') {
    const verdictEn = 'High Academic Potential, Good Character & Bright Future Prospects';
    const verdictHi = 'उच्च बौद्धिक क्षमता, सुसंस्कृत व्यक्तित्व एवं उज्ज्वल भविष्य व प्रगति';

    const analysisSummaryEn = `Your chart indicates that your children will be blessed with good intelligence, high moral character, and a natural inclination for learning. With your encouragement and a supportive home environment, they will achieve steady success in their studies and careers.`;
    const analysisSummaryHi = `आपकी संतान तीव्र बुद्धि, अच्छे संस्कार और सीखने की उत्कृष्ट ललक से युक्त होगी। वे अपनी शिक्षा और करियर में निरंतर आगे बढ़ेंगे और परिवार का मान बढ़ाएंगे।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Positive family upbringing and encouragement help unlock your children's highest talents.`,
      primaryMechanismHi: `उचित मार्गदर्शन और सकारात्मक वातावरण से बच्चे अपनी पूरी क्षमता को निखार सकेंगे।`,
      overallFeedbackEn: `Provide supportive educational environments and encourage their natural analytical/creative inclinations.`,
      overallFeedbackHi: `संतान की स्वाभाविक रुचियों को पहचानकर उन्हें उपयुक्त शैक्षणिक मार्गदर्शन और प्रोत्साहन दें।`
    };
  }

  // 7. EDUCATION THEME
  if (qId === 'education_field_stream_selection') {
    const verdictEn = factors.hasSunGovtYoga || factors.marsHouse === 10
      ? 'Strong Alignment with Engineering, Technology, Law & Public Administration'
      : factors.hasCorporateMncYoga
      ? 'High Suitability for Commerce, Data Analytics, Finance, IT & Management'
      : 'Natural Talent for Sciences, Healthcare, Research & Analytical Studies';

    const verdictHi = factors.hasSunGovtYoga || factors.marsHouse === 10
      ? 'इंजीनियरिंग, तकनीकी विज्ञान, विधि (Law) एवं प्रशासनिक क्षेत्रों में उत्तम सफलता'
      : factors.hasCorporateMncYoga
      ? 'वाणिज्य (Commerce), डेटा एनालिटिक्स, वित्त, सूचना प्रौद्योगिकी एवं प्रबंधन में उत्तम योग'
      : 'विज्ञान, चिकित्सा, शोध, अकादमिक अध्ययन एवं विश्लेषणात्मक विषयों में स्वाभाविक प्रतिभा';

    const analysisSummaryEn = `Your chart reflects sharp logical reasoning and a talent for structured problem-solving. You grasp complex concepts quickly and excel in streams where practical application meets structured analysis.`;
    const analysisSummaryHi = `आपकी बौद्धिक क्षमता विश्लेषणात्मक और तार्किक विषयों में उत्कृष्ट है। आप जटिल संकल्पनाओं को शीघ्र समझकर व्यावहारिक समाधान निकालने में निपुण हैं।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Clear thinking and disciplined study habits ensure continuous academic excellence.`,
      primaryMechanismHi: `स्पष्ट सोच और अनुशासित अध्ययन से आप उच्च शिक्षा में उत्कृष्ट प्रदर्शन करेंगे।`,
      overallFeedbackEn: `Pursue professional certifications and specialized degrees that confer distinct domain authority.`,
      overallFeedbackHi: `विशिष्ट व्यावसायिक डिग्रियों और प्रमाण पत्रों पर ध्यान केंद्रित करें जो करियर में अलग पहचान दिलाएं।`
    };
  }

  if (qId === 'education_competitive_exam_timing') {
    const verdictEn = `Optimal Window for Competitive Exams & Entrance Tests (${pStart} to ${pEnd})`;
    const verdictHi = `प्रतियोगी परीक्षाओं एवं प्रवेश परीक्षाओं में सफलता का अनुकूल समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period from ${pStart} to ${pEnd} provides high mental focus and stamina, making it an excellent time to sit for competitive tests, entrance exams, or professional certifications.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के दौरान एकाग्रता और स्मरण शक्ति तीव्र रहेगी, जिससे प्रतियोगी परीक्षाओं और महत्वपूर्ण साक्षात्कारों में उत्तम परिणाम मिलेंगे।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `High mental clarity and preparation discipline during this window maximize your performance.`,
      primaryMechanismHi: `इस समय में की गई लगनशील तैयारी आपको परीक्षा में श्रेष्ठ परिणाम दिलाएगी।`,
      overallFeedbackEn: `Adhere to rigorous mock-test routines, revise foundational concepts repeatedly, and maintain disciplined sleep hygiene.`,
      overallFeedbackHi: `नियमित मॉक टेस्ट दें, कमजोर विषयों का बार-बार अभ्यास करें और परीक्षा के दिनों में मानसिक शांति बनाए रखें।`
    };
  }

  // 8. HEALTH & VITALITY THEME
  if (qId === 'health_constitutional_strengths_vulnerabilities') {
    const verdictEn = 'Resilient Baseline Health with Focus on Balanced Diet & Stress Management';
    const verdictHi = 'सुदृढ़ शारीरिक जीवन शक्ति; संतुलित खान-पान एवं नियमित दिनचर्या पर ध्यान दें';

    const analysisSummaryEn = `You have a resilient natural constitution and good baseline energy. Maintaining regular hydration, a wholesome balanced diet, and consistent sleep will keep your energy levels steady throughout the day.`;
    const analysisSummaryHi = `आपकी स्वाभाविक शारीरिक शक्ति अच्छी है। नियमित दिनचर्या, पर्याप्त पानी, संतुलित भोजन और समय पर विश्राम से आप सदैव ऊर्जावान और स्वस्थ बने रहेंगे।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Consistent daily wellness routines and preventative care sustain your vitality over the long run.`,
      primaryMechanismHi: `दैनिक दिनचर्या और पौष्टिक आहार आपकी सेहत को दीर्घकाल तक उत्तम बनाए रखेंगे।`,
      overallFeedbackEn: `Incorporate daily walking, gentle pranayama, and mindful dietary habits into your morning routine.`,
      overallFeedbackHi: `प्रतिदिन प्रातः काल प्राणायाम और हल्का व्यायाम करें तथा अत्यधिक तले-भुने भोजन से परहेज रखें।`
    };
  }

  if (qId === 'health_timing_vigilance_recovery') {
    const verdictEn = `Health Stability & Vitality Rejuvenation Phase (${pStart} to ${pEnd})`;
    const verdictHi = `स्वास्थ्य सुधार एवं रोग-मुक्ति का अनुकूल समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period between ${pStart} and ${pEnd} is very supportive for health recovery, rebuilding physical stamina, and finding effective wellness routines.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के दौरान स्वास्थ्य में उल्लेखनीय सुधार, ऊर्जा में वृद्धि और पुरानी शारीरिक थकान से राहत मिलेगी।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Restorative astrological cycles between ${pStart} and ${pEnd} aid in quick physical recuperation.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के मध्य का समय स्वास्थ्य लाभ और ताजगी के लिए सहायक है।`,
      overallFeedbackEn: `Schedule routine annual preventative check-ups and stay consistent with healthy habits.`,
      overallFeedbackHi: `समय पर नियमित स्वास्थ्य परीक्षण कराएं और चिकित्सक के निर्देशों का पालन करें।`
    };
  }

  // 9. BUSINESS & ENTERPRISE THEME
  if (qId === 'business_solo_vs_partnership') {
    const verdictEn = factors.hasBusinessEnterpriseYoga
      ? 'High Success in Independent Business & Direct Executive Control'
      : 'Favorable Synergy in Structured Partnerships with Defined Roles';
    const verdictHi = factors.hasBusinessEnterpriseYoga
      ? 'स्वतंत्र एकल व्यवसाय एवं प्रत्यक्ष नियंत्रण में सर्वाधिक लाभ'
      : 'सुस्पष्ट कानूनी समझौतों के साथ व्यापारिक साझेदारी में उत्तम प्रगति';

    const analysisSummaryEn = `Your profile favors retaining core executive decision-making and operational control. If you choose to enter a partnership, make sure that roles, capital shares, and responsibilities are clearly defined in writing from day one.`;
    const analysisSummaryHi = `निर्णय लेने का मुख्य अधिकार अपने पास रखना आपके लिए लाभकारी रहेगा। यदि साझेदारी करें, तो जिम्मेदारियों और लाभ का स्पष्ट लिखित समझौता अवश्य करें।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Clear operational boundaries and sound financial governance ensure steady commercial success.`,
      primaryMechanismHi: `पारदर्शी कार्यप्रणाली और स्पष्ट समझौतों से व्यापार सुरक्षित रूप से आगे बढ़ेगा।`,
      overallFeedbackEn: `Draft formal legal contracts for all business associations and maintain strict cash-flow auditing.`,
      overallFeedbackHi: `सभी व्यापारिक लेन-देन का लिखित हिसाब रखें और पारदर्शी वित्तीय नीतियां अपनाएं।`
    };
  }

  if (qId === 'business_timing_launch_expansion') {
    const verdictEn = `Prime Auspicious Window for Business Launch & Expansion (${pStart} to ${pEnd})`;
    const verdictHi = `नया व्यवसाय आरंभ करने एवं व्यापार विस्तार का सर्वश्रेष्ठ शुभ समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period between ${pStart} and ${pEnd} brings positive momentum for launching new ventures, acquiring clients, and expanding commercial operations profitably.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} का समय नए प्रोजेक्ट्स शुरू करने, बाजार में विस्तार और व्यावसायिक मुनाफा बढ़ाने के लिए बहुत फलदायी रहेगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Active commercial momentum during this phase supports customer growth and revenue expansion.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के दौरान बाजार से सकारात्मक प्रतिक्रिया और लाभदायक सौदों की प्राप्ति होगी।`,
      overallFeedbackEn: `Finalize your business model, secure working capital, and launch with thorough digital/market presence.`,
      overallFeedbackHi: `इस शुभ समय में सुनियोजित रणनीति के साथ अपने उत्पाद या सेवा का विस्तार करें।`
    };
  }

  // 10. FOREIGN TRAVEL & SETTLEMENT
  if (qId === 'foreign_settlement_promise') {
    const twelfthLordH = kundali.planets?.find((p) => p.name === factors.twelfthLord)?.house || 12;
    const isForeignStrong = [12, 9, 3].includes(twelfthLordH) || [12, 9].includes(factors.rahuHouse);
    const verdictEn = isForeignStrong
      ? 'Strong Promise for Overseas Relocation & International Career Living'
      : 'High Favorability for International Projects & Frequent Overseas Travel';
    const verdictHi = isForeignStrong
      ? 'विदेश में निवास, अंतरराष्ट्रीय करियर एवं विदेशी धरती पर उन्नति का प्रबल योग'
      : 'अंतरराष्ट्रीय परियोजनाओं, बहुराष्ट्रीय कंपनियों के माध्यम से विदेश यात्रा एवं लाभ का योग';

    const analysisSummaryEn = `Your astrological indications show strong benefits from connecting with international opportunities, foreign organizations, or relocating abroad for professional and financial growth.`;
    const analysisSummaryHi = `विदेशी संपर्कों, अंतरराष्ट्रीय कंपनियों या विदेश यात्रा से आपको विशेष सम्मान और आर्थिक लाभ प्राप्त होने का सकारात्मक योग है।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `International linkages and global mobility significantly broaden your career horizon.`,
      primaryMechanismHi: `वैश्विक अवसर और अंतरराष्ट्रीय अनुभव आपके करियर को नई ऊंचाई प्रदान करेंगे।`,
      overallFeedbackEn: `Keep passport and immigration documentation ready and seek international career assignments.`,
      overallFeedbackHi: `विदेश यात्रा या वीजा से संबंधित औपचारिकताओं को समय पर पूर्ण करें।`
    };
  }

  if (qId === 'foreign_timing_visa_travel') {
    const verdictEn = `Auspicious Window for Visa Approval & International Travel (${pStart} to ${pEnd})`;
    const verdictHi = `वीजा स्वीकृति, विदेश यात्रा एवं अंतरराष्ट्रीय गमन का अनुकूल समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The period from ${pStart} to ${pEnd} is particularly supportive for smooth visa processing, resolving paperwork bottlenecks, and embarking on successful overseas travel.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के मध्य वीजा आवेदन की स्वीकृति और विदेश यात्रा में आने वाली रुकावटें दूर होकर सुगम यात्रा का योग बनेगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Supportive travel cycles between ${pStart} and ${pEnd} streamline international documentation and transit.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के दौरान विदेश यात्रा के कागजी कार्य तेजी से संपन्न होंगे।`,
      overallFeedbackEn: `Apply for visa clearances well in advance and organize travel logistics meticulously.`,
      overallFeedbackHi: `यात्रा से संबंधित सभी तैयारियों को व्यवस्थित रखें और शुभ समय में प्रस्थान करें।`
    };
  }

  // 11. LITIGATION & COMPETITION
  if (qId === 'litigation_outcome_victory' || qId === 'litigation_timing_resolution') {
    const verdictEn = `Favorable Dispute Resolution & Relief Window (${pStart} to ${pEnd})`;
    const verdictHi = `विवादों का समाधान, कानूनी राहत एवं विजय का समय (${pStart} से ${pEnd})`;

    const analysisSummaryEn = `The upcoming cycle between ${pStart} and ${pEnd} brings favorable opportunities to settle long-standing disputes, reach peaceful compromises, and eliminate legal stress.`;
    const analysisSummaryHi = `${pStart} से ${pEnd} के दौरान मामलों या विवादों में आपके पक्ष में समझौता या सकारात्मक निर्णय प्राप्त होकर मानसिक शांति मिलेगी।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Constructive astrological phases between ${pStart} and ${pEnd} foster amicable resolutions.`,
      primaryMechanismHi: `${pStart} से ${pEnd} के मध्य विवादों के शांतिपूर्ण समाधान का मार्ग प्रशस्त होगा।`,
      overallFeedbackEn: `Prefer structured arbitration or documented compromise where feasible to conserve time and resources.`,
      overallFeedbackHi: `शांतिपूर्ण एवं लिखित समझौते को प्राथमिकता देकर समय और ऊर्जा की बचत करें।`
    };
  }

  // 12. SPIRITUALITY & MOKSHA
  if (qId === 'spirituality_ishta_devata_path' || qId === 'spirituality_timing_awakening_diksha') {
    const verdictEn = 'Deep Spiritual Inclination, Mental Peace & Meditative Clarity';
    const verdictHi = 'आध्यात्मिक जागृति, मानसिक शांति एवं साधना में स्वाभाविक रुचि';

    const analysisSummaryEn = `Your chart reveals a deep natural inclination toward inner wisdom, meditation, and spiritual reflection. Daily moments of quiet contemplation, prayers, and selfless deeds will bring profound mental peace and clarity to your life.`;
    const analysisSummaryHi = `आपकी आध्यात्मिक चेतना और आंतरिक समझ बहुत प्रबुद्ध है। प्रतिदिन कुछ समय ध्यान, प्रार्थना और निस्वार्थ सेवा में लगाने से आपको असीम शांति और आत्मबल प्राप्त होगा।`;

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: `Quiet reflection, ethical living, and regular meditation sustain your inner peace and fulfillment.`,
      primaryMechanismHi: `नियमित ध्यान और सात्विक जीवनशैली से मन शांत और एकाग्र बना रहेगा।`,
      overallFeedbackEn: `Maintain daily morning reflection and engage in selfless service to nourish your spiritual well-being.`,
      overallFeedbackHi: `प्रतिदिन प्रातः काल कुछ समय एकांत में ध्यान के लिए निकालें तथा परोपकार में संलग्न रहें।`
    };
  }

  // 13. FAMILY & PARENTS
  if (qId === 'family_parents_health_harmony') {
    const verdictEn = 'Loving Family Bond & Long, Healthy Lives for Parents';
    const verdictHi = 'माता-पिता का स्वस्थ जीवन, लंबी उम्र और गहरा प्यार';

    const analysisSummaryEn = 'Both of your parents are blessed with good health, strong energy, and long, happy lives. Your mother will have steady physical strength and a peaceful, cheerful heart. Your father will have robust health, great stamina, and a long life full of vitality. Together, they create a warm, loving home where you always feel safe, guided, and cared for.';
    const analysisSummaryHi = 'आपके माता-पिता दोनों का स्वास्थ्य बहुत अच्छा रहेगा और वे लंबी, खुशहाल जिंदगी जिएंगे। आपकी माताजी के पास हमेशा अच्छी ऊर्जा और शांत मन रहेगा। आपके पिताजी की सेहत मजबूत होगी और वे लंबे समय तक स्वस्थ व सक्रिय रहेंगे। आप सबका घर प्यार, आपसी समझ और एक-दूसरे के लिए देखभाल से भरा रहेगा।';

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: 'A strong foundation of physical wellness and deep emotional love between family members.',
      primaryMechanismHi: 'शारीरिक तंदुरुस्ती और परिवार के सदस्यों के बीच गहरे भावनात्मक प्यार की मजबूत नींव।',
      overallFeedbackEn: 'Talk to your parents every day, make sure elders eat well and visit doctors on time, and always value their kind advice.',
      overallFeedbackHi: 'माता-पिता से रोज़ बात करें, बुजुर्गों की सेहत का पूरा ध्यान रखें और उनके अच्छे सुझावों का हमेशा सम्मान करें।'
    };
  }

  if (qId === 'family_ancestral_heritage_happiness') {
    const verdictEn = 'Smooth Ancestral Inheritance & Peaceful Family Home';
    const verdictHi = 'पुश्तैनी संपत्ति की प्राप्ति और घर में सुख-शांति';

    const analysisSummaryEn = 'You have very clear and positive chances of receiving ancestral property, family land, or savings passed down through generations without any major disputes. Your family wealth and assets will remain secure, helping you build a stable future. Your home life will stay peaceful, happy, and full of joyful family gatherings.';
    const analysisSummaryHi = 'आपको अपने पूर्वजों की तरफ से जमीन, मकान या पुश्तैनी संपत्ति बिना किसी बड़े झगड़े के आसानी से मिलने के पूरे योग हैं। आपके परिवार की संपत्ति और जमा-पूंजी सुरक्षित रहेगी, जिससे आपका भविष्य मजबूत बनेगा। आपका घर हमेशा शांतिपूर्ण, खुशहाल और परिवार के प्यार से भरा रहेगा।';

    return {
      verdictEn,
      verdictHi,
      analysisSummaryEn,
      analysisSummaryHi,
      primaryMechanismEn: 'Smooth passing of family wealth paired with a calm and happy home environment.',
      primaryMechanismHi: 'पारिवारिक संपत्ति का सुगम हस्तांतरण और एक शांत व खुशहाल घरेलू माहौल।',
      overallFeedbackEn: 'Respect family traditions, take good care of family property and heirlooms, and keep your home filled with love and laughter.',
      overallFeedbackHi: 'पारिवारिक परंपराओं का आदर करें, पुश्तैनी संपत्ति की देखभाल अच्छे से करें और अपने घर में हमेशा प्यार और हंसी-खुशी का माहौल बनाए रखें।'
    };
  }

  // Fallback for custom or unmapped questions
  const isTimingQuestion = question.category === 'TIMING' || question.timingRelevant === true;
  const verdictEn = isTimingQuestion
    ? `Favorable Window for Progress & Constructive Results (${pStart} to ${pEnd})`
    : 'Positive Potential & Natural Growth Across This Life Area';
  const verdictHi = isTimingQuestion
    ? `सकारात्मक प्रगति एवं अनुकूल परिणामों का समय (${pStart} से ${pEnd})`
    : 'इस क्षेत्र में सकारात्मक संभावना एवं स्वाभाविक उन्नति का योग';

  const analysisSummaryEn = `Your birth chart indicates positive natural support for this area of life. With focused effort and steady planning, you will experience constructive growth and fulfilling outcomes.`;
  const analysisSummaryHi = `आपकी जन्म कुंडली इस क्षेत्र में सकारात्मक सहयोग दर्शाती है। निरंतर प्रयास और सुनियोजित दृष्टिकोण से आपको उत्तम और संतोषजनक परिणाम प्राप्त होंगे।`;

  return {
    verdictEn,
    verdictHi,
    analysisSummaryEn,
    analysisSummaryHi,
    primaryMechanismEn: isTimingQuestion
      ? `The active window between ${pStart} and ${pEnd} provides supportive momentum for this goal.`
      : `Consistent planning and dedication will steadily yield the best results.`,
    primaryMechanismHi: isTimingQuestion
      ? `${pStart} से ${pEnd} के मध्य का समय इस उद्देश्य की पूर्ति के लिए अनुकूल अवसर प्रदान करेगा।`
      : `योजनाबद्ध प्रयास और निष्ठा से आपको निरंतर उत्तम परिणाम प्राप्त होंगे।`,
    overallFeedbackEn: `Take disciplined steps and maintain a clear, positive approach to achieve optimal results.`,
    overallFeedbackHi: `सकारात्मक दृष्टिकोण और योजनाबद्ध तरीके से आगे बढ़ें।`
  };
}
